import { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { Home, Menu, X, Phone, Mail } from 'lucide-react';
import { ROUTE_PATHS } from '@/lib/index';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';

interface LayoutProps {
  children: React.ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { to: ROUTE_PATHS.HOME, label: 'Trang Chủ' },
    { to: ROUTE_PATHS.LEGAL, label: 'Hồ Sơ Pháp Lý' },
    { to: ROUTE_PATHS.ELIGIBILITY, label: 'Đối Tượng Đăng Ký' },
    { to: ROUTE_PATHS.ANNOUNCEMENT, label: 'Công Bố Thông Tin' },
    { to: ROUTE_PATHS.PROGRESS, label: 'Tiến Độ' },
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? 'bg-primary text-primary-foreground shadow-lg'
            : 'bg-transparent text-foreground'
        }`}
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <NavLink
              to={ROUTE_PATHS.HOME}
              className="flex items-center gap-2 font-semibold hover:opacity-80 transition-opacity"
            >
              <Home className="w-6 h-6 flex-shrink-0" />
              <div className="leading-tight">
                <div className="text-sm font-bold">Harmony Xuân Phương</div>
                <div className="text-xs opacity-70 hidden sm:block">OCT1 &amp; OCT3 – Vietasset</div>
              </div>
            </NavLink>

            <nav className="hidden md:flex items-center gap-6">
              {navLinks.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  className={({ isActive }) =>
                    `text-sm font-medium transition-colors hover:opacity-80 ${
                      isActive ? 'underline underline-offset-4' : ''
                    }`
                  }
                >
                  {link.label}
                </NavLink>
              ))}
            </nav>

            <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
              <SheetTrigger asChild className="md:hidden">
                <Button variant="ghost" size="icon">
                  <Menu className="w-6 h-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[280px]">
                <div className="flex flex-col gap-4 mt-8">
                  {navLinks.map((link) => (
                    <NavLink
                      key={link.to}
                      to={link.to}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={({ isActive }) =>
                        `text-base font-medium py-2 px-4 rounded-md transition-colors ${
                          isActive
                            ? 'bg-primary text-primary-foreground'
                            : 'hover:bg-accent hover:text-accent-foreground'
                        }`
                      }
                    >
                      {link.label}
                    </NavLink>
                  ))}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      <main className="flex-1 pt-16">{children}</main>

      <footer className="bg-muted border-t border-border mt-auto">
        <div className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <h3 className="font-semibold text-lg mb-4">Thông Tin Dự Án</h3>
              <div className="flex items-start gap-2 mb-3">
                <Home className="w-5 h-5 mt-0.5 text-primary flex-shrink-0" />
                <div>
                  <p className="font-medium text-sm">NOXH Cho LLVT CAND – Xuân Phương</p>
                  <p className="text-xs text-muted-foreground mt-0.5">OCT1 &amp; OCT3, Phường Xuân Phương, TP. Hà Nội</p>
                  <p className="text-xs text-muted-foreground">CĐT: Vietasset – Constrexim Số 8</p>
                  <p className="text-xs text-muted-foreground">818 căn hộ | 25 tầng nổi + 3 hầm</p>
                  <p className="text-xs text-muted-foreground">QII/2025 – QIV/2028</p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-lg mb-4">Liên Kết Nhanh</h3>
              <ul className="space-y-2">
                {navLinks.map((link) => (
                  <li key={link.to}>
                    <NavLink
                      to={link.to}
                      className="text-sm text-muted-foreground hover:text-primary transition-colors"
                    >
                      {link.label}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-lg mb-4">Liên Hệ</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-primary" />
                  <span className="text-sm">Hotline: 0917978466</span>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-primary" />
                  <span className="text-sm">Email: info@vietasset.vn</span>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-border mt-8 pt-6 text-center">
            <p className="text-sm text-muted-foreground">
              © 2026 NOXH Xuân Phương – Công ty CP Đầu tư Vietasset. Tất cả quyền được bảo lưu.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}